/** RainViewer public Weather Maps API (no API key). */

const RAINVIEWER_MAPS_URL = 'https://api.rainviewer.com/public/weather-maps.json'

export type RainViewerRadarFrame = {
  time: number
  path: string
  host: string
}

type RainViewerMapsResponse = {
  host?: string
  radar?: {
    past?: Array<{ time: number; path: string }>
    nowcast?: Array<{ time: number; path: string }>
  }
}

/** Default color scheme + smoothed/snow options per RainViewer docs. */
export const RAINVIEWER_TILE_COLOR = 2
export const RAINVIEWER_TILE_OPTIONS = '1_1'
export const RAINVIEWER_TILE_SIZE = 256
export const RAINVIEWER_MAX_ZOOM = 7

export function buildRainViewerTileUrlTemplate(frame: RainViewerRadarFrame): string {
  const host = frame.host.replace(/\/$/, '')
  const path = frame.path.startsWith('/') ? frame.path : `/${frame.path}`
  return `${host}${path}/${RAINVIEWER_TILE_SIZE}/{z}/{x}/{y}/${RAINVIEWER_TILE_COLOR}/${RAINVIEWER_TILE_OPTIONS}.png`
}

export function buildRainViewerTileUrl(
  frame: RainViewerRadarFrame,
  z: number,
  x: number,
  y: number,
): string {
  const host = frame.host.replace(/\/$/, '')
  const path = frame.path.startsWith('/') ? frame.path : `/${frame.path}`
  const zoom = Math.max(0, Math.min(RAINVIEWER_MAX_ZOOM, Math.round(z)))
  return `${host}${path}/${RAINVIEWER_TILE_SIZE}/${zoom}/${x}/${y}/${RAINVIEWER_TILE_COLOR}/${RAINVIEWER_TILE_OPTIONS}.png`
}

export function formatRainViewerFrameTime(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

type MapLike = {
  getZoom: () => number
  getBounds: () => {
    getNorthWest: () => { lat: number; lng: number }
    getSouthEast: () => { lat: number; lng: number }
  }
  project: (
    latlng: { lat: number; lng: number },
    zoom: number,
  ) => { x: number; y: number }
}

/** Soft Image() preload — uses browser HTTP cache, no CORS fetch. */
const preloadInflight = new Set<string>()
const PRELOAD_MAX_CONCURRENT = 2
let preloadActive = 0
const preloadQueue: Array<() => void> = []

function runPreloadQueue() {
  while (preloadActive < PRELOAD_MAX_CONCURRENT && preloadQueue.length > 0) {
    const next = preloadQueue.shift()
    if (next) next()
  }
}

function preloadImageUrl(url: string): void {
  if (preloadInflight.has(url)) return
  preloadInflight.add(url)
  const start = () => {
    preloadActive += 1
    const img = new Image()
    const finish = () => {
      preloadActive -= 1
      preloadInflight.delete(url)
      runPreloadQueue()
    }
    img.onload = finish
    img.onerror = finish
    img.src = url
  }
  if (preloadActive < PRELOAD_MAX_CONCURRENT) start()
  else preloadQueue.push(start)
}

/** Warm the next frame’s viewport tiles via Image() (browser cache, no CORS). */
export function preloadRainViewerFrameTiles(map: MapLike, frame: RainViewerRadarFrame): void {
  const zoom = Math.max(0, Math.min(RAINVIEWER_MAX_ZOOM, Math.round(map.getZoom())))
  const bounds = map.getBounds()
  const nw = map.project(bounds.getNorthWest(), zoom)
  const se = map.project(bounds.getSouthEast(), zoom)
  const minX = Math.floor(Math.min(nw.x, se.x) / RAINVIEWER_TILE_SIZE)
  const maxX = Math.floor(Math.max(nw.x, se.x) / RAINVIEWER_TILE_SIZE)
  const minY = Math.floor(Math.min(nw.y, se.y) / RAINVIEWER_TILE_SIZE)
  const maxY = Math.floor(Math.max(nw.y, se.y) / RAINVIEWER_TILE_SIZE)
  // Cap how many we warm so a continental view doesn’t stampede the CDN.
  const urls: string[] = []
  for (let x = minX; x <= maxX; x += 1) {
    for (let y = minY; y <= maxY; y += 1) {
      urls.push(buildRainViewerTileUrl(frame, zoom, x, y))
    }
  }
  const capped = urls.slice(0, 16)
  for (const url of capped) preloadImageUrl(url)
}

let mapsJsonCache: { at: number; frames: RainViewerRadarFrame[] } | null = null
const MAPS_JSON_TTL_MS = 60_000

/** Past frames (~2h) plus nowcast when present, oldest → newest. */
export async function fetchRainViewerRadarFrames(
  signal?: AbortSignal,
): Promise<RainViewerRadarFrame[]> {
  if (mapsJsonCache && Date.now() - mapsJsonCache.at < MAPS_JSON_TTL_MS) {
    return mapsJsonCache.frames
  }

  const res = await fetch(RAINVIEWER_MAPS_URL, { signal })
  if (!res.ok) {
    throw new Error(`RainViewer maps error (${res.status})`)
  }
  const data = (await res.json()) as RainViewerMapsResponse
  const host = String(data.host || 'https://tilecache.rainviewer.com').replace(/\/$/, '')
  const past = Array.isArray(data.radar?.past) ? data.radar!.past! : []
  const nowcast = Array.isArray(data.radar?.nowcast) ? data.radar!.nowcast! : []
  const frames: RainViewerRadarFrame[] = [...past, ...nowcast]
    .filter(
      (f) =>
        f &&
        typeof f.time === 'number' &&
        Number.isFinite(f.time) &&
        typeof f.path === 'string' &&
        f.path.length > 0,
    )
    .map((f) => ({ time: f.time, path: f.path, host }))
    .sort((a, b) => a.time - b.time)

  const byTime = new Map<number, RainViewerRadarFrame>()
  for (const frame of frames) byTime.set(frame.time, frame)
  const sorted = Array.from(byTime.values()).sort((a, b) => a.time - b.time)
  mapsJsonCache = { at: Date.now(), frames: sorted }
  return sorted
}

export const RAINVIEWER_ATTRIBUTION =
  '<a href="https://www.rainviewer.com/" target="_blank" rel="noopener noreferrer">RainViewer</a>'
